import React, { useState } from "react";
import {
  Button,
  DatePicker,
  Input,
  Select,
  Table,
  Spin,
  Modal,
  notification,
  Dropdown,
} from "antd";
import {
  SearchOutlined,
  CloseOutlined,
  MoreOutlined,
  EditOutlined,
  DeleteOutlined,
  PlayCircleOutlined,
  StopOutlined,
  MinusCircleOutlined,
} from "@ant-design/icons";
import { LuFilter } from "react-icons/lu";
import { useNavigate, useParams } from "react-router-dom";
import { LayoutGrid, List } from "lucide-react";
import moment from "moment";
import { ACTIONS_DROPDOWN_PROPS } from "../../constants/actionsDropdown";
import {
  useGetCampaignByIdQuery,
  useGetCampaignStatsQuery,
  useGetFormStatsByIdQuery,
  useGetSubmitToOrganizationStatsQuery,
  useDeleteFormMutation,
  useUpdateFormStatusMutation,
  useAddFormToCampaignMutation,
  useRemoveFormFromCampaignMutation,
  useUpdateCampaignMutation,
  useActivateFormMutation,
  useDeactivateFormMutation,
} from "../../store/api/apiSlice";
import AddFormModal from "../Modals/AddFormModal";
import CreateFormModal from "../Modals/CreateFormModal";
import { useSelector } from "react-redux";
import { selectCurrentRole } from "../../store/auth/authSlice";
import { getStatusColor, getStatusDisplayText } from "../../utils";
import { withDerivedCampaignStatus } from "../../utils/campaigns";

const formatStatusLabel = (status) => getStatusDisplayText(status) || "—";

const toFiniteNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const getFormDetailsStats = (stats, isOrganizationUser) => {
  const questions = toFiniteNumber(stats?.total_questions);
  const responses = toFiniteNumber(
    isOrganizationUser ? stats?.responses_received : stats?.total_responses,
  );
  const ratio = toFiniteNumber(
    stats?.response_ratio ?? stats?.response_rate ?? 0,
  );

  return {
    questions,
    responses,
    rate: `${ratio.toFixed(1)}%`,
  };
};

const useCampaignFormDetailsStats = (formId) => {
  const userRole = useSelector(selectCurrentRole);
  const isOrganizationUser = userRole === "organization";
  const { data: statsData } = useGetFormStatsByIdQuery(formId, {
    skip: !formId || isOrganizationUser,
  });
  const { data: orgStatsData } = useGetSubmitToOrganizationStatsQuery(formId, {
    skip: !formId || !isOrganizationUser,
  });

  const stats = isOrganizationUser ? orgStatsData?.data : statsData?.data;
  return getFormDetailsStats(stats, isOrganizationUser);
};

const CampaignQuestionsCell = ({ formId }) => {
  const { questions } = useCampaignFormDetailsStats(formId);
  return <span>Questions {questions}</span>;
};

const CampaignResponsesCell = ({ formId }) => {
  const { responses, rate } = useCampaignFormDetailsStats(formId);
  return (
    <span>
      {responses} ({rate})
    </span>
  );
};

const CampaignGridFormStats = ({ formId }) => {
  const { questions, responses, rate } = useCampaignFormDetailsStats(formId);
  return (
    <div className="mt-4 flex justify-between items-center">
      <div className="text-sm">Questions {questions}</div>
      <div className="text-sm">
        Responses: {responses} ({rate})
      </div>
    </div>
  );
};

const CampaignDetails = () => {
  const { id: campaignId } = useParams();
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");
  const [dateRange, setDateRange] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const [viewType, setViewType] = useState(isMobile ? "grid" : "list");
  const [addFormModalVisible, setAddFormModalVisible] = useState(false);
  const [editFormModalVisible, setEditFormModalVisible] = useState(false);
  const [editingForm, setEditingForm] = useState(null);
  const userRole = useSelector(selectCurrentRole);
  const basePath = window.location.pathname.split("/")[1];

  const goToFormDetails = (form) => {
    if (!form?._id) return;

    navigate(`/${basePath}/forms/${form._id}`, {
      state: form,
    });
  };

  // API Hooks
  const {
    data: campaignData,
    isLoading,
    refetch: refetchCampaign,
  } = useGetCampaignByIdQuery(campaignId);
  const { data: statsData } = useGetCampaignStatsQuery();
  const [deleteForm] = useDeleteFormMutation();
  const [updateFormStatus] = useUpdateFormStatusMutation();
  const [activateForm] = useActivateFormMutation();
  const [deactivateForm] = useDeactivateFormMutation();
  const [addFormToCampaign] = useAddFormToCampaignMutation();
  const [removeFormFromCampaign] = useRemoveFormFromCampaignMutation();
  const [updateCampaign] = useUpdateCampaignMutation();

  // Filter forms based on search and filters
  const filteredForms = campaignData?.data?.forms?.filter((form) => {
    const matchesSearch =
      !searchText ||
      form.title.toLowerCase().includes(searchText.toLowerCase()) ||
      form.description?.toLowerCase().includes(searchText.toLowerCase());
    const matchesStatus = !statusFilter || form.status === statusFilter;
    const matchesDate =
      !dateRange ||
      (moment(form.start_date).isSameOrAfter(dateRange[0], "day") &&
        moment(form.end_date).isSameOrBefore(dateRange[1], "day"));
    return matchesSearch && matchesStatus && matchesDate;
  });

  const handleFormDelete = (formId) => {
    Modal.confirm({
      title: "Delete Form",
      content: "Are you sure you want to delete this form?",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          await deleteForm(formId).unwrap();
          // Update campaign forms array
          const updatedForms = campaignData.data.forms.filter(
            (f) => f._id !== formId
          );
          await updateCampaign({
            id: campaignId,
            data: { ...campaignData.data, forms: updatedForms },
          }).unwrap();
          notification.success({
            message: "Success",
            description: "Form deleted successfully",
          });
          refetchCampaign();
        } catch (error) {
          notification.error({
            message: "Error",
            description: error.message || "Failed to delete form",
          });
        }
      },
    });
  };

  const handleStatusChange = async (form, newStatus) => {
    try {
      if (newStatus === "active") {
        await activateForm(form._id).unwrap();
      } else if (newStatus === "not_active") {
        await deactivateForm(form._id).unwrap();
      } else {
        // For other statuses, use the old updateFormStatus
        await updateFormStatus({
          id: form._id,
          status: newStatus,
        }).unwrap();
      }

      // Update form status in campaign
      const updatedForms = campaignData.data.forms.map((f) =>
        f._id === form._id ? { ...f, status: newStatus } : f
      );
      await updateCampaign({
        id: campaignId,
        data: { ...campaignData.data, forms: updatedForms },
      }).unwrap();

      if (newStatus !== "active" && newStatus !== "not_active") {
        notification.success({
          message: "Success",
          description: `Form status updated to ${newStatus}`,
        });
      }
      refetchCampaign();
    } catch (error) {
      notification.error({
        message: "Error",
        description: error.message || "Failed to update status",
      });
    }
  };

  const handleRemoveFromCampaign = async (formId) => {
    try {
      // await removeFormFromCampaign({ campaignId, formId }).unwrap();
      // Update campaign forms array
      // const updatedForms = campaignData.data.forms.filter(f => f._id !== formId);
      // await updateCampaign({
      //   id: campaignId,
      //   data: {  forms:[formId]}
      // }).unwrap();
      const updatedFormIds = campaignData.data.forms
        .filter((f) => f._id !== formId)
        .map((f) => f._id);

      await updateCampaign({
        id: campaignId,
        data: { forms: updatedFormIds },
      }).unwrap();

      notification.success({
        message: "Success",
        description: "Form removed from campaign successfully",
      });
      refetchCampaign();
    } catch (error) {
      notification.error({
        message: "Error",
        description: error.message || "Failed to remove form",
      });
    }
  };

  const handleEditForm = (form) => {
    setEditingForm(form);
    setEditFormModalVisible(true);
  };

  const getFormActionsMenuItems = (record) => {
    const items = [
      { key: "edit", label: "Edit", icon: <EditOutlined /> },
      {
        key: "remove",
        label: "Remove",
        icon: <MinusCircleOutlined />,
      },
      {
        key: "delete",
        label: "Delete",
        danger: true,
        icon: <DeleteOutlined />,
      },
    ];

    if (record.status === "active") {
      items.splice(2, 0, { key: "end", label: "End", icon: <StopOutlined /> });
    } else if (record.status === "draft" || record.status === "not_active") {
      items.splice(2, 0, {
        key: "start",
        label: "Start",
        icon: <PlayCircleOutlined />,
      });
    }

    return items;
  };

  const handleFormActionMenuClick = (record, actionKey, event) => {
    event?.domEvent?.stopPropagation?.();

    switch (actionKey) {
      case "edit":
        handleEditForm(record);
        break;
      case "delete":
        handleFormDelete(record._id);
        break;
      case "remove":
        handleRemoveFromCampaign(record._id);
        break;
      case "start":
        handleStatusChange(record, "active");
        break;
      case "end":
        handleStatusChange(record, "not_active");
        break;
      default:
        break;
    }
  };

  const renderActionsDropdown = (record) => (
    <div
      className="flex items-center justify-center"
      onClick={(e) => e.stopPropagation()}
    >
      <Dropdown
        {...ACTIONS_DROPDOWN_PROPS}
        menu={{
          items: getFormActionsMenuItems(record),
          onClick: (event) =>
            handleFormActionMenuClick(record, event.key, event),
        }}
      >
        <button
          type="button"
          className="actions-dots-btn"
          onClick={(e) => e.stopPropagation()}
          aria-label={`Open actions for ${record?.title || "form"}`}
        >
          <MoreOutlined />
        </button>
      </Dropdown>
    </div>
  );

  const columns = [
    {
      title: "Sr#",
      width: 70,
      render: (_, __, index) => index + 1,
    },

    {
      title: "Form",
      key: "form",
      render: (_, record) => (
        <div>
          <div className="font-medium text-gray-900">{record.title}</div>
          <div className="text-sm text-gray-500 truncate max-w-xl">
            {record.description}
          </div>
          <span className="inline-block px-3 py-1 border text-sm rounded-full mt-1">
            {record.type}
          </span>
        </div>
      ),
    },
    {
      title: "Questions",
      render: (record) => <CampaignQuestionsCell formId={record._id} />,
    },
    {
      title: "Responses",
      render: (record) => <CampaignResponsesCell formId={record._id} />,
    },
    {
      title: "Status",
      render: (_, record) => (
        <span
          className={`inline-flex rounded-full px-3 py-1 text-sm capitalize ${getStatusColor(
            record.status
          )}`}
        >
          {formatStatusLabel(record.status)}
        </span>
      ),
    },
    userRole === "admin"
      ? {
          title: "",
          key: "actions",
          width: 80,
          align: "center",
          render: (_, record) => renderActionsDropdown(record),
        }
      : {},
  ];

  const GridView = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {filteredForms?.map((form) => (
        <div
          key={form._id}
          className="bg-white p-6 rounded-xl border border-gray-200 hover:shadow-lg transition-shadow cursor-pointer"
          onClick={() => goToFormDetails(form)}
        >
          <div>
            <div className="font-medium text-gray-900">{form.title}</div>
            <div className="text-sm text-gray-500 line-clamp-2">
              {form.description}
            </div>
            <span className="inline-block px-3 py-1 border text-sm rounded-full mt-1">
              {form.type}
            </span>
          </div>

          <CampaignGridFormStats formId={form._id} />

          <div className="mt-4 flex justify-between items-center">
            <span
              className={`inline-flex rounded-full px-3 py-1 text-sm capitalize ${getStatusColor(
                form.status
              )}`}
            >
              {formatStatusLabel(form.status)}
            </span>
            {userRole === "admin" && renderActionsDropdown(form)}
          </div>
        </div>
      ))}
    </div>
  );

  const StatCard = ({ value, label }) => (
    <div className="bg-[#F9FAFB] rounded-xl p-6">
      <div className="space-y-1">
        <div className="text-2xl font-bold text-gray-900">{value}</div>
        <div className="text-sm text-gray-500">{label}</div>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spin size="large" />
      </div>
    );
  }

  const campaign = withDerivedCampaignStatus(campaignData?.data);

  const overviewStats = [
    { value: campaign?.forms?.length || 0, label: "Total Forms" },
    {
      value:
        campaign?.forms?.filter((f) => f.status === "active")
          .length || 0,
      label: "Active Forms",
    },
    { value: statsData?.data?.totalResponses || 0, label: "Total Responses" },
    { value: `${Number(statsData?.data?.responseRate || 0).toFixed(1)}%`, label: "Response Rate" },
  ];

  return (
    <div className="space-y-6 mt-3">
      {/* Campaign Info */}
      <div className="bg-white rounded-xl border p-6">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {campaign?.title}
            </h2>
            <p className="text-gray-500 text-sm mt-1 max-w-3xl">
              {campaign?.description}
            </p>
          </div>
          <span
            className={`inline-flex rounded-full px-3 py-1 text-sm capitalize ${getStatusColor(
              campaign?.status
            )}`}
          >
            {formatStatusLabel(campaign?.status)}
          </span>
        </div>
      </div>

      {/* Overview Stats */}
      <div>
        <div className="flex justify-between items-center mb-2 selectSecondary">
          <h2 className="text-md font-semibold m-0">Overview </h2>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-2">
          {overviewStats.map((stat, index) => (
            <StatCard key={index} {...stat} />
          ))}
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex items-center gap-4 mb-6 flex-wrap">
        <div className="flex bg-secondary rounded-xl p-1.5">
          <button
            className={`view-toggle-btn ${
              viewType === "list" ? "bg-primary text-white" : ""
            }`}
            onClick={() => setViewType("list")}
          >
            <List size={20} />
          </button>
          <button
            className={`view-toggle-btn ${
              viewType === "grid" ? "bg-primary text-white" : ""
            }`}
            onClick={() => setViewType("grid")}
          >
            <LayoutGrid size={20} />
          </button>
        </div>

        <div className="relative flex-1">
          <Input
            placeholder="Search..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            prefix={<SearchOutlined className="text-gray-400" />}
            suffix={
              searchText ? (
                <CloseOutlined
                  className="text-gray-400 cursor-pointer"
                  onClick={() => setSearchText("")}
                />
              ) : null
            }
            className="h-11 rounded-xl bg-white"
          />
        </div>

        <DatePicker.RangePicker
          value={dateRange}
          className="h-11 w-full md:w-[340px] !rounded-xl [&_.ant-picker-input>input]:text-xs md:[&_.ant-picker-input>input]:text-sm [&_.ant-picker-input]:flex [&_.ant-picker-input]:items-center [&_.ant-picker-separator]:flex [&_.ant-picker-separator]:items-center"
          onChange={setDateRange}
          format="DD/MM/YYYY"
          placeholder={["Start date", "End date"]}
          allowClear
          popupClassName="date-range-popup-mobile"
          getPopupContainer={() => document.body}
          dropdownAlign={{
            points: ["tl", "bl"],
            offset: [0, 4],
            overflow: {
              adjustX: 1,
              adjustY: 1,
            },
          }}
        />

        <Select
          placeholder="Status"
          value={statusFilter}
          onChange={setStatusFilter}
          className="w-[78%] md:w-32 h-11 rounded-xl"
          options={[
            { value: "", label: "All Status" },
            { value: "active", label: "Active" },
            { value: "complete", label: "Completed" },
            { value: "draft", label: "Draft" },
            { value: "not_active", label: "Inactive" },
          ]}
        />

        <button className="h-11 w-11 flex items-center justify-center rounded-xl bg-secondary border border-red-500">
          <LuFilter size={20} className="text-red-500" />
        </button>
      </div>
      {/* Forms Header */}

      {userRole === "admin" && (
        <div className="flex justify-between items-center">
          <h2 className="text-md font-semibold m-0">Campaign Forms</h2>
          <Button
            onClick={() => setAddFormModalVisible(true)}
            className="h-11 bg-primary text-white rounded-xl px-4"
          >
            Add Form
          </Button>
        </div>
      )}

      {/* Forms List/Grid */}
      {viewType === "list" ? (
        <Table
          columns={columns}
          dataSource={filteredForms}
          rowKey="_id"
          pagination={false}
          rowClassName="cursor-pointer"
          onRow={(record) => ({
            onClick: () => goToFormDetails(record),
          })}
          scroll={{ x: true }}
        />
      ) : (
        <GridView />
      )}

      {/* Add Form Modal */}
      <AddFormModal
        visible={addFormModalVisible}
        onClose={() => setAddFormModalVisible(false)}
        campaignId={campaignId}
        onSuccess={() => {
          setAddFormModalVisible(false);
          refetchCampaign();
        }}
      />

      {/* Edit Form Modal */}
      <CreateFormModal
        visible={editFormModalVisible}
        onClose={() => {
          setEditFormModalVisible(false);
          setEditingForm(null);
        }}
        editData={editingForm}
        onSuccess={() => {
          setEditFormModalVisible(false);
          setEditingForm(null);
          refetchCampaign();
        }}
      />
    </div>
  );
};

export default CampaignDetails;
